import React, { FormEventHandler, useState } from 'react';
import axios from 'axios';
import { apiRoute } from '../../../constants/api-constants';
import { useAuth } from '../../../context/auth-provider';
import { RightArrow } from '../../icons/right-arrow';
import { Shake, StatusLabel } from '../../../motion';
import {
  Field,
  FieldRow,
  Label,
  Input,
  PrimaryButton,
  ButtonRow,
  FieldError,
} from '../onboarding.styled';

// The required first step for manager/employee/tenant flows: confirm name +
// phone. Saves via PUT /api/profile (self-scoped), then advances onboarding.
export const ProfileStep: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const { user, setUser } = useAuth();
  const parts = (user?.full_name || '').trim().split(/\s+/);
  const [firstName, setFirstName] = useState(parts[0] || '');
  const [lastName, setLastName] = useState(parts.slice(1).join(' '));
  const [phone, setPhone] = useState(user?.phone_number || '');
  const [err, setErr] = useState('');
  // Counts refusals so the name fields shake again on a second empty submit,
  // not only on the first.
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setErr('First and last name are required');
      setAttempt(n => n + 1);

      return;
    }
    setErr('');
    setSaving(true);
    try {
      const res = await axios.put(`${apiRoute}/profile`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: phone.trim(),
      });
      if (user) {
        setUser({
          ...user,
          full_name: res.data.full_name,
          phone_number: res.data.phone_number,
        });
      }
      onSaved();
    } catch {
      setErr('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    void submit();
  };

  return (
    <form onSubmit={onSubmit}>
      <FieldRow>
        <Field>
          <Label htmlFor="profile-first-name">First name</Label>
          <Shake active={!!err} attempt={attempt}>
            <Input
              id="profile-first-name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              hasError={!!err}
            />
          </Shake>
        </Field>
        <Field>
          <Label htmlFor="profile-last-name">Last name</Label>
          <Shake active={!!err} attempt={attempt}>
            <Input
              id="profile-last-name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              hasError={!!err}
            />
          </Shake>
        </Field>
      </FieldRow>
      <Field>
        <Label htmlFor="profile-phone">Phone (optional)</Label>
        <Input
          id="profile-phone"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="+1 555 000 0000"
        />
      </Field>
      <FieldError message={err || undefined} />
      <ButtonRow>
        <span />
        <PrimaryButton type="submit" disabled={saving}>
          <StatusLabel
            state={saving ? 'busy' : 'idle'}
            labels={{ idle: 'Save & continue', busy: 'Saving…' }}
          />{' '}
          <RightArrow />
        </PrimaryButton>
      </ButtonRow>
    </form>
  );
};
